import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BrandMark, Button, EmptyState, Skeleton, useToast } from "@guestpilot/ui-kit";
import { CheckmarkCircle32Filled, LockClosed16Regular } from "@fluentui/react-icons";
import { ApiProblemError } from "../api/client";
import { useGet, useWrite } from "../api/hooks";
import type { Schema, Subscription as Sub } from "../api/types";
import { useHandleApiError } from "../errors";
import { I18nProvider, useI18n } from "../i18n";
import { setBearerOverride } from "../teams";
import { CardBody, PurchaseBody } from "./Subscription";

type Context = Schema<"PaymentContext">;

/**
 * The card payment, in the browser, outside Teams (the Teams Store forbids payments inside Teams, D-165). The tab opens this page with a
 * single-use link; the link, in the URL fragment, is the only credential of the page. When the payment is done the page says so and
 * offers the way back to Teams; the tab, waiting, has already updated itself.
 */
export function PayPage() {
  // the link, in the fragment (it never reaches a server log), is the bearer of every call of this page; a reload keeps working
  const token = useMemo(() => {
    const t = window.location.hash.replace(/^#/, "");
    setBearerOverride(t || undefined);
    return t;
  }, []);
  useEffect(() => () => setBearerOverride(undefined), []);
  const ctx = useGet<Context>("/api/v1/pay/context", undefined, { enabled: Boolean(token) });
  const locale = ctx.data?.locale ?? (typeof navigator === "undefined" ? "en" : navigator.language);
  return (
    <I18nProvider locale={locale}>
      <div className="gp-pay-page">
        <div className="gp-pay-card">
          <header className="gp-pay-head">
            <BrandMark size={36} />
            <span className="t-title-sm">GuestPilot</span>
          </header>
          {!token || ctx.isError ? (
            <Expired />
          ) : ctx.isLoading || !ctx.data ? (
            <Skeleton rows={6} />
          ) : (
            <Content ctx={ctx.data} />
          )}
        </div>
      </div>
    </I18nProvider>
  );
}

function Expired() {
  const { t } = useI18n();
  return (
    <div className="gp-stack">
      <EmptyState title={t("ui:subscription.payExpired")} />
      <p className="t-body gp-muted" style={{ margin: 0, textAlign: "center" }}>
        {t("ui:subscription.payExpiredLead")}
      </p>
    </div>
  );
}

function Done({ ctx }: { ctx: Context }) {
  const { t } = useI18n();
  const card = ctx.purpose === "card";
  return (
    <div className="gp-pay-done" role="status">
      <CheckmarkCircle32Filled aria-hidden style={{ color: "var(--success-text)" }} />
      <h1 className="t-title-md" style={{ margin: 0 }}>
        {t(card ? "ui:subscription.payCardDone" : "ui:subscription.payDone")}
      </h1>
      <p className="t-body gp-muted" style={{ margin: 0 }}>
        {t(card ? "ui:subscription.payCardDoneLead" : "ui:subscription.payDoneLead")}
      </p>
      <a className="gp-btn gp-btn--primary gp-btn--lg" href={ctx.returnUrl}>
        {t("ui:subscription.payReturn")}
      </a>
    </div>
  );
}

function Content({ ctx }: { ctx: Context }) {
  const { t } = useI18n();
  const toast = useToast();
  const fail = useHandleApiError();
  const qc = useQueryClient();
  const [done, setDone] = useState(ctx.used);
  const sub = useGet<Sub>("/api/v1/subscription");
  const subscribe = useWrite<Sub, Schema<"SubscribeRequest">>("POST", "/api/v1/subscription/subscribe");
  const update = useWrite<Sub, { paymentMethodId: string }>("PUT", "/api/v1/subscription/payment-method");
  // a link past its time answers 401 on the next call: say it plainly
  const onError = (e: unknown) => {
    if (e instanceof ApiProblemError && e.status === 401) setDone(false);
    fail(e);
  };
  const plan = (sub.data?.plans ?? []).find((p) => p.planKey === ctx.planKey);
  if (done) return <Done ctx={ctx} />;
  return (
    <div className="gp-stack">
      <div className="gp-stack" style={{ gap: "var(--space-4)" }}>
        <h1 className="t-title-md" style={{ margin: 0 }}>
          {ctx.purpose === "card"
            ? t("ui:subscription.payCardTitle")
            : t("ui:subscription.purchaseTitle", { plan: plan?.displayName ?? "" })}
        </h1>
        <p className="t-body gp-muted" style={{ margin: 0 }}>
          {t("ui:subscription.payFor", { org: ctx.organizationName })}
        </p>
        <p className="t-body-sm gp-muted gp-row" style={{ margin: 0 }}>
          <LockClosed16Regular aria-hidden /> {t("ui:subscription.payLead")}
        </p>
      </div>
      {ctx.purpose === "card" ? (
        <CardBody
          onPaymentMethod={async (pm) => {
            try {
              await update.mutateAsync({ body: { paymentMethodId: pm } });
              toast.success(t("ui:settings.saved"));
              setDone(true);
            } catch (e) {
              onError(e);
            }
          }}
        />
      ) : sub.isLoading ? (
        <Skeleton rows={4} />
      ) : plan && ctx.billingInterval ? (
        <PurchaseBody
          plan={plan}
          interval={ctx.billingInterval}
          email={sub.data?.billingContactEmail ?? ""}
          subscribe={async (pm) => {
            try {
              await subscribe.mutateAsync({
                body: {
                  planKey: plan.planKey as "starter" | "business" | "scale",
                  billingInterval: ctx.billingInterval!,
                  paymentMethodId: pm,
                },
              });
              void qc.invalidateQueries({ queryKey: ["/api/v1/subscription"] });
              setDone(true);
            } catch (e) {
              onError(e);
            }
          }}
        />
      ) : (
        <div className="gp-stack">
          <p className="t-body">{t("ui:subscription.payUnknownPlan")}</p>
          <Button onClick={() => window.close()}>{t("common:close")}</Button>
        </div>
      )}
    </div>
  );
}
